import { useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Network } from '@root/entities/network'
import { isTauriAtom, PlatformUtil } from '@root/shared'
import { CopyButton, Truncate } from '@root/shared/ui-kit'

import type { Address } from '@root/entities/address/Address'

type Props = {
  address: Address
  network: Network.Network
  /**
   * What the address itself does when clicked.
   *
   * Given one, the address is a button rather than a link out: a wallet has
   * a profile of ours worth more than the explorer, and the explorer is one
   * click away inside it. Without one it stays a link, which is right for a
   * pair or a contract, neither of which we hold a profile for.
   */
  onClick?: () => void
}

export const AuditContractItem: React.FC<Props> = ({
  address,
  network,
  onClick,
}) => {
  const { t } = useTranslation()
  const [isTauri] = useAtom(isTauriAtom)

  if (!address) {
    return (
      <Root>
        <Value>{t('common.n_a')}</Value>
      </Root>
    )
  }
  const explorerLink = Network.makeExplorerContractLink(network, address)

  if (onClick) {
    return (
      <Root>
        <ValueButton type="button" onClick={onClick}>
          <Truncate start={6} end={6}>
            {address}
          </Truncate>
        </ValueButton>

        <CopyButton value={address} />
      </Root>
    )
  }

  return (
    <Root>
      <ValueLink
        onClick={() => {
          if (isTauri) {
            PlatformUtil.openLink(explorerLink, '_blank')
          }
        }}
        href={explorerLink}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Truncate start={6} end={6}>
          {address}
        </Truncate>
      </ValueLink>

      <CopyButton value={address} />
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  gap: 2px;
`
const Value = styled.div``
const ValueLink = styled(Value).attrs({ as: 'a' })``

const ValueButton = styled.button`
  padding: 0;
  border: none;
  background: none;

  color: inherit;
  font: inherit;
  cursor: pointer;

  &:hover {
    color: var(--text-12);
    text-decoration: underline;
  }
`
